import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { getMarketing } from '../content/marketing';
import heroFlowA from '../assets/images/hero-flow-a.jpg';
import heroFlowB from '../assets/images/hero-flow-b.jpg';
import networkFlow from '../assets/images/network-flow.jpg';
import '../styles/site.css';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const copy = getMarketing(i18n.language).landing;
  const [ready, setReady] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const rootRef = useRef(null);
  const heroImages = [heroFlowA, heroFlowB];

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const nodes = root.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [i18n.language]);

  const platforms = ['Windows', 'macOS', 'Linux', 'Android', 'iOS'];

  return (
    <div ref={rootRef} className={`arc-site arc-landing ${ready ? 'is-ready' : ''}`}>
      <SiteNav transparent />

      <section className="arc-hero">
        <div className="arc-hero-stage">
          {heroImages.map((src, index) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`arc-hero-media ${heroIndex === index ? 'is-active' : ''}`}
            />
          ))}
        </div>
        <div className="arc-hero-veil" aria-hidden="true" />
        <div className="arc-hero-grade" aria-hidden="true" />
        <div className="arc-hero-ambient" aria-hidden="true">
          <span className="arc-orb arc-orb-a" />
          <span className="arc-orb arc-orb-b" />
          <span className="arc-signal-line" />
        </div>
        <div className="arc-hero-copy">
          <p className="arc-brand-mark">ArcVPN</p>
          <h1>{copy.title}</h1>
          <p className="arc-hero-lead">{copy.description}</p>
          <div className="arc-hero-cta">
            <button type="button" className="arc-btn arc-btn-solid arc-btn-lg" onClick={() => navigate('/signup')}>
              {copy.cta}
            </button>
            <button type="button" className="arc-btn arc-btn-line arc-btn-lg" onClick={() => navigate('/features')}>
              {i18n.language?.startsWith('tr') ? 'Özellikler' : 'Features'}
            </button>
          </div>
          <div className="arc-hero-dots" aria-hidden="true">
            {heroImages.map((_, index) => (
              <button
                key={index}
                type="button"
                className={heroIndex === index ? 'is-active' : ''}
                onClick={() => setHeroIndex(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="arc-section arc-container" data-reveal>
        <div className="arc-section-head">
          <h2>{copy.featuresTitle}</h2>
          <p>{copy.featuresSubtitle}</p>
        </div>
        <div className="arc-feature-list">
          {copy.features.map((feature, index) => (
            <article className="arc-feature-item" style={{ '--i': index }} key={feature.title} data-reveal>
              <span className="arc-feature-index">0{index + 1}</span>
              <div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="arc-section arc-container" data-reveal>
        <div className="arc-section-head">
          <h2>{copy.platformsTitle}</h2>
          <p>{copy.platformsSubtitle}</p>
        </div>
        <ul className="arc-platform-row">
          {platforms.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </section>

      <section className="arc-section arc-container" data-reveal>
        <div className="arc-section-head">
          <h2>{copy.networkTitle}</h2>
          <p>{copy.networkSubtitle}</p>
        </div>
        <figure className="arc-network-figure">
          <img src={networkFlow} alt={copy.networkTitle} />
          <div className="arc-network-sheen" aria-hidden="true" />
        </figure>
      </section>

      <section className="arc-section arc-container" data-reveal>
        <div className="arc-section-head">
          <h2>{copy.useTitle}</h2>
          <p>{copy.useSubtitle}</p>
        </div>
        <div className="arc-usecase-list">
          {copy.useCases.map((item, index) => (
            <article key={item.title} className="arc-usecase-item" style={{ '--i': index }} data-reveal>
              <span>0{index + 1}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="arc-cta-band is-centered" data-reveal>
        <div className="arc-container arc-cta-inner">
          <h2>{copy.ctaTitle}</h2>
          <p>{copy.ctaDescription}</p>
          <button type="button" className="arc-btn arc-btn-solid arc-btn-lg" onClick={() => navigate('/signup')}>
            {copy.ctaButton}
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Landing;
